/**
 * Detection sensor catalog — Optex initial seed (2026-05-13).
 *
 * Built from datasheets the customer forwarded. Phase 1 ships the three
 * pattern types fully supported by PIRFOVRenderer today:
 *   - 'cone'     (wide-angle wall PIR: VX, HX, QXI, VXI, WXI)
 *   - 'full'     (ceiling 360°: CX-702, FX-360)
 *   - 'boundary' (two opposite curtains from one wall sensor: BX Shield)
 *   - 'curtain'  (single narrow curtain near windows: FlipX)
 *
 * Pattern types deferred to Phase 2/3 — entries are present in the catalog
 * but `phase2Beam` / `phase3Fence` flags hide them from the picker until
 * implemented:
 *   - 'beam'  (point-to-point pair: AX-70/130/200, SL-100/200 TNR)
 *   - 'fence' (along existing Fence entity: FTN series)
 *
 * Specs lifted from the datasheets, conservative defaults where the
 * datasheet doesn't pin a single number. Range values are the maximum
 * stated; the user can dial it down per instance via the slider.
 */
import type { PIRPattern } from './equipmentTypes';
// Safe despite the apparent cycle: fallDetectionCatalog imports only a TYPE
// from this module, which is erased at compile time — no runtime cycle.
import { FALL_DETECTION_SENSORS } from './fallDetectionCatalog';

export type DetectionSensorPattern = PIRPattern | 'beam' | 'fence';

export interface DetectionSensorCatalogEntry {
  /** Stable, kebab-case ID used as the foreign key from Equipment.catalogId. */
  id: string;
  manufacturer: string;
  model: string;
  pattern: DetectionSensorPattern;

  /** What kind of detection — for grouping in the picker. */
  category:
    | 'outdoor-wall-pir'
    | 'indoor-ceiling-pir'
    | 'curtain-pir'
    | 'boundary-pir'
    | 'beam-barrier'
    | 'fence-detector'
    // mmWave fall-detection radar (Hikvision DS-TDSB00, care/AAL sector).
    // Reuses the PIR placement machinery: ceiling units render the 'full'
    // 360° disc (ceilingDiameterByHeightM), wall units the 'cone' wedge.
    | 'fall-detection-radar';

  /** Detection range in metres. For 'beam' this is point-to-point distance. */
  rangeM: number;
  /** Selectable range steps (Optex VX Shield, BX Shield offer 5 positions). */
  rangeStepsM?: number[];
  /** Horizontal coverage angle in degrees. 360 for 'full', ~3 for 'curtain'. */
  hfovDeg: number;
  /**
   * Ceiling-mount diameter as a function of mounting height — only
   * meaningful for pattern='full' (ceiling 360°). FX-360 datasheet:
   * 8 m at 2.4 m, 10 m at 3 m, 12 m at 3.6 m. Renderer linearly
   * interpolates between the two adjacent keys.
   *
   * When undefined or for non-ceiling patterns the renderer falls back
   * to the static `rangeM` field (typical case for wall PIRs whose
   * detection range doesn't change with mounting height).
   */
  ceilingDiameterByHeightM?: Record<number, number>;
  /**
   * Mounting height range in metres (wall sensors: 0.8-1.2; ceiling: 2.4-3.6).
   * Default 1 (wall) or 2.7 (ceiling) gets seeded into the Equipment instance.
   */
  mountingHeightDefaultM: number;
  mountingHeightMinM: number;
  mountingHeightMaxM: number;
  mounting: Array<'wall' | 'pole' | 'ceiling' | 'fence'>;

  /** Detection method, surfaced in the picker subtitle. */
  detectionMethod:
    | 'pir'
    | 'pir-mw-dual'
    | 'photoelectric'
    | 'laser'
    | 'fence-tension'
    | 'radar';

  // Optional spec fields:
  antiMasking?: boolean;
  petImmune?: boolean;
  batteryOperated?: boolean;
  ipRating?: string;
  operatingTempMinC?: number;
  operatingTempMaxC?: number;
  powerVMin?: number;
  powerVMax?: number;
  currentMaTypical?: number;
  outputType?: 'NC_relay' | 'NO_relay' | 'NC_NO_selectable' | 'solid_state';

  // Reference material:
  datasheetUrl?: string;
  /** Short marketing line for the picker card. */
  tagline: string;

  // Phase-gating: hide unfinished pattern types from the picker.
  phase2Beam?: boolean;
  phase3Fence?: boolean;
}

export const OPTEX_DETECTION_SENSORS: DetectionSensorCatalogEntry[] = [
  // ── Outdoor wall PIR (wide-angle cone) ───────────────────────────────────
  {
    id: 'optex-vxs-am',
    manufacturer: 'Optex',
    model: 'VXS-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 24,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° wide-angle outdoor PIR with anti-masking',
  },
  {
    id: 'optex-vxs-dam',
    manufacturer: 'Optex',
    model: 'VXS-DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 35,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° PIR + microwave dual-tech, anti-masking',
  },
  {
    id: 'optex-hx-40am',
    manufacturer: 'Optex',
    model: 'HX-40AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9, 5.5, 4],
    // Datasheet correction (2026-05-13): HX-40 is a HIGH-MOUNT detector,
    // not a low-mount like VX Shield. Mount height 2.5-3.0 m gives the
    // best volumetric pattern for the 85° × 12 m coverage.
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 40,
    outputType: 'NC_NO_selectable',
    tagline: 'High-mount 12 m × 85° outdoor PIR, anti-masking (HX-40AM)',
  },
  {
    id: 'optex-qxi-st',
    manufacturer: 'Optex',
    model: 'QXI-ST',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    // Datasheet correction (2026-05-13): QXI is 120° wide, not 90°. IP54,
    // not IP55. Supports dual mounting modes (multi-level 2.2-2.7 m OR
    // pet-alley 0.8-1.2 m). Default to multi-level — most common for
    // outdoor commercial.
    hfovDeg: 120,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP54',
    operatingTempMinC: -40,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 20,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 120° wide-angle outdoor PIR with quad-element AND logic',
  },
  {
    id: 'optex-vxi-st',
    manufacturer: 'Optex',
    model: 'VXI-ST',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    // 5-step selectable range matches the physical DIP switch on the
    // sensor (datasheet calls it "Position 1-5").
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 20,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° outdoor PIR, 16-zone, 5-step range select (VXI-ST)',
  },
  {
    id: 'optex-wxi-st',
    manufacturer: 'Optex',
    model: 'WXI-ST',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    // Datasheet correction (2026-05-13): WXI is a 180° PANORAMIC PIR
    // (wall-to-wall coverage), NOT a 90° wedge. And it's pure PIR, not
    // dual-tech — I had detectionMethod='pir-mw-dual' incorrectly seeded
    // from the "W" in the model name (W is for "wide", not "wave").
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 21,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 180° panoramic wall-to-wall outdoor PIR (WXI-ST)',
  },

  // ── Indoor ceiling 360° (full circle) ────────────────────────────────────
  {
    id: 'optex-fx-360',
    manufacturer: 'Optex',
    model: 'FX-360',
    pattern: 'full',
    category: 'indoor-ceiling-pir',
    rangeM: 6, // radius default (used as fallback when height interp absent)
    // Datasheet: 8 m diameter @ 2.4 m, 10 m @ 3 m, 12 m @ 3.6 m.
    // Renderer reads this when pattern='full' so the rendered radius
    // shrinks/grows as the user adjusts mounting height.
    ceilingDiameterByHeightM: { 2.4: 8, 3.0: 10, 3.6: 12 },
    hfovDeg: 360,
    mountingHeightDefaultM: 3.0,
    mountingHeightMinM: 2.4,
    mountingHeightMaxM: 3.6,
    mounting: ['ceiling'],
    detectionMethod: 'pir',
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 17,
    outputType: 'NC_relay',
    tagline: '360° ceiling PIR, 8-12 m diameter (varies with mounting height)',
  },
  // CX-702 reclassified (2026-05-13): I originally seeded this as a
  // ceiling 360° pet-immune PIR, but the actual datasheet describes a
  // WALL-MOUNT detector with a rotatable lens — switchable between
  // Wide Angle (21 × 21 m @ 85°, 68 zones) and Long Range (45 × 2.4 m
  // narrow corridor, 22 zones). Mounting height 1.5-3.6 m, wall-mount.
  // The "pet-immune" flag and 360° pattern were my fabrication. Default
  // to wide-angle mode below — long-range mode would be a separate
  // 'curtain'-style entry if we split the SKU.
  {
    id: 'optex-cx-702',
    manufacturer: 'Optex',
    model: 'CX-702',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 21,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.6,
    mounting: ['wall'],
    detectionMethod: 'pir',
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 8,
    outputType: 'NC_relay',
    tagline: 'Wall PIR with rotatable lens: 21 m × 85° wide OR 45 m corridor',
  },

  // ── Boundary (two opposing curtains from a single wall sensor) ──────────
  {
    id: 'optex-bxs-st',
    manufacturer: 'Optex',
    model: 'BXS-ST',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 8, // narrow per-side curtain
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 31,
    outputType: 'NC_NO_selectable',
    tagline: '24 m boundary (12 m each side) along a wall',
  },
  {
    id: 'optex-bxs-am',
    manufacturer: 'Optex',
    model: 'BXS-AM',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 8,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 34,
    outputType: 'NC_NO_selectable',
    tagline: '24 m boundary with anti-masking',
  },

  // ── Curtain / narrow PIR ────────────────────────────────────────────────
  {
    id: 'optex-flipx-standard',
    manufacturer: 'Optex',
    model: 'FlipX Standard',
    pattern: 'cone', // Wide mode is the default rotatable-lens position
    category: 'curtain-pir',
    rangeM: 12,
    // Datasheet correction (2026-05-13): FlipX has a rotatable lens with
    // TWO modes — WIDE (12 m × 85°) and NARROW (18 m × 5°). Default
    // catalog entry uses the wide mode since that's the shipping default.
    // Users who want the narrow-corridor mode rotate the lens physically;
    // a future catalog split could expose both as separate SKUs.
    hfovDeg: 85,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'ceiling'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 12,
    outputType: 'NC_relay',
    tagline: 'Indoor PIR with rotatable lens: 12 m × 85° wide OR 18 m × 5° narrow',
  },
  // FTN reclassified (2026-05-13): I originally seeded FTN-ST as a
  // 'fence-tension' detector based on the "FTN" name, but the actual
  // Optex "fit" series datasheet describes it as a COMPACT OUTDOOR
  // narrow-curtain PIR (5 m × 1 m, switchable to 2 m, mounted 0.8-1.2 m
  // on a wall — typically next to a window, balcony, or sidewalk). The
  // "fence-tension" classification was my fabrication. The Phase 3
  // fence-snap renderer + auto-attach logic stays in the codebase
  // (deactivated) for when Optex Smart Line / REDSCAN fence-tension
  // products get added in a future expansion.
  {
    id: 'optex-ftn-st',
    manufacturer: 'Optex',
    model: 'FTN-ST',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2], // switchable lens, datasheet "5m/2m switchable"
    hfovDeg: 12, // narrow 5×1 m projection ≈ 12° at 5 m
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 17,
    outputType: 'NC_NO_selectable',
    tagline: 'Compact outdoor narrow PIR, 5 m × 1 m (window/balcony/sidewalk)',
  },

  // ── Beam barrier (paired transmitter + receiver) — Phase 2 ──────────────
  {
    id: 'optex-ax-70tn',
    manufacturer: 'Optex',
    model: 'AX-70TN',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 20,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 38,
    outputType: 'NC_relay',
    tagline: '20 m short-range twin-beam barrier (TX + RX pair)',
  },
  {
    id: 'optex-ax-130tn',
    manufacturer: 'Optex',
    model: 'AX-130TN',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 40,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 41,
    outputType: 'NC_relay',
    tagline: '40 m twin-beam barrier (TX + RX pair)',
  },
  {
    id: 'optex-ax-200tn',
    manufacturer: 'Optex',
    model: 'AX-200TN',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 45,
    outputType: 'NC_relay',
    tagline: '60 m twin-beam barrier (TX + RX pair)',
  },
  {
    id: 'optex-sl-100tnr',
    manufacturer: 'Optex',
    model: 'SL-100 TNR',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP65',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_relay',
    tagline: '30 m wireless twin-beam (battery-powered, no cabling)',
  },
  {
    id: 'optex-sl-200tnr',
    manufacturer: 'Optex',
    model: 'SL-200 TNR',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    batteryOperated: true,
    ipRating: 'IP65',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    outputType: 'NC_relay',
    tagline: '60 m wireless twin-beam (battery-powered, no cabling)',
  },

  // NOTE: A previous catalog draft seeded an FTN-ST entry as a
  // fence-tension detector here. The actual datasheet
  // (optex-ftn-st_ftn-r_ftn-ram_ftn-am-datasheet.pdf) describes the
  // "fit" series — compact outdoor narrow PIRs (5 m × 1 m or 2 m × 1 m).
  // The real FTN-ST is now in the curtain-pir group above. Optex DOES
  // manufacture fence-tension and laser perimeter detectors (Smart Line,
  // REDSCAN, etc.), but no datasheet for those is in the current catalog
  // folder. When we add Smart Line, it lands here as 'fence' pattern
  // and the existing renderer overlay + addEquipment auto-snap flow
  // takes over without code changes.

  // ─────────────────────────────────────────────────────────────────────
  // Variant expansion (2026-06-19) — full Optex series matrices from the
  // datasheet pack. Each variant CLONES the verified sibling above and
  // changes only the distinguishing fields (detection method, anti-mask,
  // battery vs wired, output). Geometry (range / FOV / mount / category)
  // is inherited from the seed entries so the placement footprint stays
  // datasheet-accurate. Suffix key: AM=anti-mask, DAM/DT=dual-tech (PIR+MW),
  // R=battery/wireless, RAM=battery+anti-mask, RDAM=battery+dual+anti-mask.
  // Beam variants (AX-100TF/AX-200TF) are intentionally NOT added — they
  // would be hidden by the phase2Beam picker gate (no beam renderer yet).
  // ─────────────────────────────────────────────────────────────────────

  // ── VX Shield (VXS) — battery variants of the seed VXS-AM/DAM ──────────
  {
    id: 'optex-vxs-ram',
    manufacturer: 'Optex',
    model: 'VXS-RAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: 'VX Shield 12 m × 90° battery PIR, anti-masking (no cabling)',
  },
  {
    id: 'optex-vxs-rdam',
    manufacturer: 'Optex',
    model: 'VXS-RDAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 45,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 8,
    outputType: 'solid_state',
    tagline: 'VX Shield 12 m × 90° battery PIR+MW dual-tech, anti-masking',
  },

  // ── VX Infinity (VXI) — full matrix off the seed VXI-ST ────────────────
  {
    id: 'optex-vxi-am',
    manufacturer: 'Optex',
    model: 'VXI-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 24,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° outdoor PIR with active-IR anti-masking (VXI-AM)',
  },
  {
    id: 'optex-vxi-dam',
    manufacturer: 'Optex',
    model: 'VXI-DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 45,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 35,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 90° PIR+microwave dual-tech with anti-masking (VXI-DAM)',
  },
  {
    id: 'optex-vxi-r',
    manufacturer: 'Optex',
    model: 'VXI-R',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '12 m × 90° battery/wireless outdoor PIR (VXI-R)',
  },
  {
    id: 'optex-vxi-ram',
    manufacturer: 'Optex',
    model: 'VXI-RAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '12 m × 90° battery outdoor PIR with anti-masking (VXI-RAM)',
  },
  {
    id: 'optex-vxi-rdam',
    manufacturer: 'Optex',
    model: 'VXI-RDAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 90,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 45,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 8,
    outputType: 'solid_state',
    tagline: '12 m × 90° battery PIR+MW dual-tech, anti-masking (VXI-RDAM)',
  },

  // ── WX Infinity (WXI) — 180° panoramic, full matrix off WXI-ST ─────────
  {
    id: 'optex-wxi-am',
    manufacturer: 'Optex',
    model: 'WXI-AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 23,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 180° panoramic outdoor PIR with anti-masking (WXI-AM)',
  },
  {
    id: 'optex-wxi-r',
    manufacturer: 'Optex',
    model: 'WXI-R',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 3.6,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '12 m × 180° panoramic battery/wireless outdoor PIR (WXI-R)',
  },
  {
    id: 'optex-wxi-ram',
    manufacturer: 'Optex',
    model: 'WXI-RAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 180,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 3.6,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '12 m × 180° panoramic battery PIR with anti-masking (WXI-RAM)',
  },

  // ── QX Infinity (QXI) — dual-tech + battery variants off QXI-ST ────────
  {
    id: 'optex-qxi-dt',
    manufacturer: 'Optex',
    model: 'QXI-DT',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 120,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP54',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 28,
    outputType: 'NC_NO_selectable',
    tagline: '12 m × 120° PIR+microwave dual-tech, anti-masking (QXI-DT)',
  },
  {
    id: 'optex-qxi-r',
    manufacturer: 'Optex',
    model: 'QXI-R',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 120,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP54',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '12 m × 120° battery/wireless outdoor PIR (QXI-R)',
  },
  {
    id: 'optex-qxi-rdt',
    manufacturer: 'Optex',
    model: 'QXI-RDT',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    hfovDeg: 120,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 2.7,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP54',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 8,
    outputType: 'solid_state',
    tagline: '12 m × 120° battery PIR+MW dual-tech, anti-masking (QXI-RDT)',
  },

  // ── HX-40 high-mount — wired/dual/battery variants off HX-40AM ─────────
  {
    id: 'optex-hx-40',
    manufacturer: 'Optex',
    model: 'HX-40',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9, 5.5, 4],
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 36,
    outputType: 'NC_NO_selectable',
    tagline: 'High-mount 12 m × 85° outdoor dual-PIR, wired (HX-40)',
  },
  {
    id: 'optex-hx-40dam',
    manufacturer: 'Optex',
    model: 'HX-40DAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9, 5.5, 4],
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir-mw-dual',
    antiMasking: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 50,
    outputType: 'NC_NO_selectable',
    tagline: 'High-mount 12 m × 85° dual-PIR + microwave, anti-masking (HX-40DAM)',
  },
  {
    id: 'optex-hx-40ram',
    manufacturer: 'Optex',
    model: 'HX-40RAM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 12,
    rangeStepsM: [12, 9, 5.5, 4],
    hfovDeg: 85,
    mountingHeightDefaultM: 2.7,
    mountingHeightMinM: 2.5,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 7.2,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: 'High-mount 12 m × 85° battery dual-PIR, anti-masking (HX-40RAM)',
  },

  // ── BX Shield (BXS) — battery boundary variants off BXS-ST/AM ──────────
  {
    id: 'optex-bxs-r',
    manufacturer: 'Optex',
    model: 'BXS-R',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 8,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '24 m battery boundary (12 m each side), wireless (BXS-R)',
  },
  {
    id: 'optex-bxs-ram',
    manufacturer: 'Optex',
    model: 'BXS-RAM',
    pattern: 'boundary',
    category: 'boundary-pir',
    rangeM: 12,
    rangeStepsM: [12, 8.5, 6, 3.5, 2.5],
    hfovDeg: 8,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall', 'pole'],
    detectionMethod: 'pir',
    antiMasking: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -30,
    operatingTempMaxC: 60,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 4,
    outputType: 'solid_state',
    tagline: '24 m battery boundary with anti-masking, wireless (BXS-RAM)',
  },

  // ── CX-702 — battery indoor variant off CX-702 ─────────────────────────
  {
    id: 'optex-cx-702rs',
    manufacturer: 'Optex',
    model: 'CX-702RS',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 21,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.5,
    mountingHeightMaxM: 3.6,
    mounting: ['wall'],
    detectionMethod: 'pir',
    batteryOperated: true,
    ipRating: 'Indoor',
    operatingTempMinC: -10,
    operatingTempMaxC: 50,
    powerVMin: 3,
    powerVMax: 9,
    currentMaTypical: 10,
    outputType: 'solid_state',
    tagline: 'Battery indoor PIR, rotatable lens: 21 m × 85° wide OR 45 m corridor (CX-702RS)',
  },

  // ── FlipX — dual-tech variant off FlipX Standard ───────────────────────
  {
    id: 'optex-flx-s-dt',
    manufacturer: 'Optex',
    model: 'FLX-S-DT',
    pattern: 'cone',
    category: 'curtain-pir',
    rangeM: 12,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.5,
    mountingHeightMinM: 2.0,
    mountingHeightMaxM: 3.0,
    mounting: ['wall', 'ceiling'],
    detectionMethod: 'pir-mw-dual',
    petImmune: true,
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9.5,
    powerVMax: 16,
    currentMaTypical: 16,
    outputType: 'NC_relay',
    tagline: 'Indoor dual-tech (PIR+MW) rotatable-lens PIR, pet immune (FLX-S-DT)',
  },

  // ── FTN "fit" series — anti-mask + battery variants off FTN-ST ─────────
  {
    id: 'optex-ftn-am',
    manufacturer: 'Optex',
    model: 'FTN-AM',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2],
    hfovDeg: 12,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 9.5,
    powerVMax: 18,
    currentMaTypical: 20,
    outputType: 'NC_NO_selectable',
    tagline: 'Compact outdoor 5 m × 1 m curtain PIR, wired with anti-masking (FTN-AM)',
  },
  {
    id: 'optex-ftn-r',
    manufacturer: 'Optex',
    model: 'FTN-R',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2],
    hfovDeg: 12,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 2.5,
    powerVMax: 10,
    currentMaTypical: 3,
    outputType: 'solid_state',
    tagline: 'Compact outdoor 5 m × 1 m battery curtain PIR, wireless (FTN-R)',
  },
  {
    id: 'optex-ftn-ram',
    manufacturer: 'Optex',
    model: 'FTN-RAM',
    pattern: 'curtain',
    category: 'curtain-pir',
    rangeM: 5,
    rangeStepsM: [5, 2],
    hfovDeg: 12,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.8,
    mountingHeightMaxM: 1.2,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    petImmune: true,
    batteryOperated: true,
    ipRating: 'IP55',
    operatingTempMinC: -20,
    operatingTempMaxC: 60,
    powerVMin: 2.5,
    powerVMax: 10,
    currentMaTypical: 3,
    outputType: 'solid_state',
    tagline: 'Compact outdoor 5 m × 1 m battery curtain PIR, anti-masking (FTN-RAM)',
  },

  // ── AX-TF beam barriers (newer 4-channel gen of the AX-TN twin-beams) ──
  // Same placeable TX/RX twin-beam flow as the AX-*TN above (pattern 'beam'
  // is in SUPPORTED_PATTERNS_PHASE1, no phase flag → visible in the picker).
  // The TF series adds 4-channel frequency selection (stack up to 4 stages
  // without crosstalk) and a Disqualification/trouble output.
  {
    id: 'optex-ax-100tf',
    manufacturer: 'Optex',
    model: 'AX-100TF',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 30,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 44,
    outputType: 'NC_NO_selectable',
    tagline: '30 m twin-beam barrier, 4-channel freq + trouble output (AX-100TF)',
  },
  {
    id: 'optex-ax-200tf',
    manufacturer: 'Optex',
    model: 'AX-200TF',
    pattern: 'beam',
    category: 'beam-barrier',
    rangeM: 60,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.0,
    mountingHeightMinM: 0.5,
    mountingHeightMaxM: 4.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'photoelectric',
    ipRating: 'IP65',
    operatingTempMinC: -35,
    operatingTempMaxC: 60,
    powerVMin: 10.5,
    powerVMax: 28,
    currentMaTypical: 48,
    outputType: 'NC_NO_selectable',
    tagline: '60 m twin-beam barrier, 4-channel freq + trouble output (AX-200TF)',
  },

  // ── CX-502 / CX-502AM — indoor 82-zone dual-purpose PIR (CX family) ─────
  // Smaller sibling of the CX-702 above: 15 × 15 m wide-angle standard, OR
  // 24 m corridor with the optional CL-80N lens. Quad Zone Logic, Photo-MOS
  // relay output, indoor. CX-502AM adds Multi anti-masking. Specs from the
  // CX-502/502AM datasheet (no PDF in the zip — sourced from Optex docs).
  {
    id: 'optex-cx-502',
    manufacturer: 'Optex',
    model: 'CX-502',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9,
    powerVMax: 18,
    currentMaTypical: 14,
    outputType: 'NC_relay',
    tagline: 'Indoor 82-zone PIR: 15 × 15 m wide OR 24 m corridor (optional lens)',
  },
  {
    id: 'optex-cx-502am',
    manufacturer: 'Optex',
    model: 'CX-502AM',
    pattern: 'cone',
    category: 'outdoor-wall-pir',
    rangeM: 15,
    hfovDeg: 85,
    mountingHeightDefaultM: 2.4,
    mountingHeightMinM: 1.8,
    mountingHeightMaxM: 3.0,
    mounting: ['wall'],
    detectionMethod: 'pir',
    antiMasking: true,
    ipRating: 'Indoor',
    operatingTempMinC: -20,
    operatingTempMaxC: 50,
    powerVMin: 9,
    powerVMax: 18,
    currentMaTypical: 16,
    outputType: 'NC_relay',
    tagline: 'Indoor 82-zone PIR with multi anti-masking: 15 × 15 m / 24 m corridor (CX-502AM)',
  },

  // ── JVA — electric fence energiser + zone monitor (perimeter security) ────
  // Jumbo ZM50 Sector Loop Monitor: combined HV energiser + fence monitor, up
  // to 50 sectors with patented Distant Fault Detection (DFD™). Placed on a
  // Fence entity (pattern 'fence'). detectionMethod 'fence-tension' is the
  // nearest catalog category for a live perimeter-fence line — the real
  // mechanism is a high-voltage electric fence. rangeM/hfovDeg are nominal:
  // the detection follows the attached fence polyline, not a cone.
  {
    id: 'jva-jmb32-zm50',
    manufacturer: 'JVA',
    model: 'JMB32ZM50',
    pattern: 'fence',
    category: 'fence-detector',
    rangeM: 1000,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.5,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 2.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'fence-tension',
    ipRating: 'IP54',
    powerVMin: 12,
    powerVMax: 24,
    currentMaTypical: 3500,
    outputType: 'NC_NO_selectable',
    datasheetUrl: 'http://www.jva-fence.com/JMB',
    tagline: 'Jumbo ZM50 energiser + monitor — 9.5 kV, 32 J output / 46 J stored, up to 50 sectors, DFD™ (PTE2400)',
  },
  {
    id: 'jva-jmb25-zm50',
    manufacturer: 'JVA',
    model: 'JMB25ZM50',
    pattern: 'fence',
    category: 'fence-detector',
    rangeM: 1000,
    hfovDeg: 0,
    mountingHeightDefaultM: 1.5,
    mountingHeightMinM: 1.0,
    mountingHeightMaxM: 2.0,
    mounting: ['wall', 'pole'],
    detectionMethod: 'fence-tension',
    ipRating: 'IP54',
    powerVMin: 12,
    powerVMax: 24,
    currentMaTypical: 3500,
    outputType: 'NC_NO_selectable',
    datasheetUrl: 'http://www.jva-fence.com/JMB',
    tagline: 'Jumbo ZM50 energiser + monitor — 9.5 kV, 25 J output / 36 J stored, up to 50 sectors, DFD™ (PTE2403)',
  },
];

export function getDetectionSensorById(
  id: string,
): DetectionSensorCatalogEntry | undefined {
  return (
    OPTEX_DETECTION_SENSORS.find((s) => s.id === id) ??
    FALL_DETECTION_SENSORS.find((s) => s.id === id)
  );
}

/**
 * For ceiling-mount detectors: compute the rendered detection RADIUS in
 * metres at the given mounting height by interpolating the catalog's
 * `ceilingDiameterByHeightM` map.
 *
 * Returns null when the catalog row doesn't define the map (use rangeM
 * directly), or when the entry isn't a ceiling pattern. The renderer
 * falls back to eq.pirRange in that case.
 *
 * Linear interpolation between the two adjacent key heights. Clamps
 * outside the defined range (e.g. mounting at 2.0 m on a sensor whose
 * smallest tabulated height is 2.4 m → returns the 2.4 m diameter).
 */
export function ceilingRadiusAtHeight(
  catalogId: string | undefined,
  mountingHeightM: number | undefined,
): number | null {
  if (!catalogId || mountingHeightM == null) return null;
  const entry = getDetectionSensorById(catalogId);
  if (!entry || entry.pattern !== 'full') return null;
  const table = entry.ceilingDiameterByHeightM;
  if (!table) return null;
  const heights = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (heights.length === 0) return null;

  // Clamp below the smallest tabulated height.
  if (mountingHeightM <= heights[0]) {
    return table[heights[0]] / 2;
  }
  // Clamp above the largest.
  if (mountingHeightM >= heights[heights.length - 1]) {
    return table[heights[heights.length - 1]] / 2;
  }
  // Linear interpolation between the two adjacent keys.
  for (let i = 1; i < heights.length; i++) {
    const lo = heights[i - 1];
    const hi = heights[i];
    if (mountingHeightM >= lo && mountingHeightM <= hi) {
      const t = (mountingHeightM - lo) / (hi - lo);
      const diameter = table[lo] + t * (table[hi] - table[lo]);
      return diameter / 2;
    }
  }
  return null;
}

/** Pattern types fully supported by the renderer today. */
export const SUPPORTED_PATTERNS_PHASE1: DetectionSensorPattern[] = [
  'cone',
  'full',
  'boundary',
  'curtain',
  'beam',  // Phase 2 — TX/RX pair flow via consumePendingBeamTx
  'fence', // Phase 3 — auto-snap to nearest Fence entity via addEquipment
];

// ────────────────────────────────────────────────────────────────────────────
// Pending-catalog handshake.
//
// The flow we want is:
//   1. User clicks "Detection sensor (Optex)" in the toolbar
//   2. Picker dialog opens, they choose a model
//   3. Dialog closes; tool switches to 'pir-motion-sensor'
//   4. User clicks on map → addEquipment runs and pre-configures the
//      Equipment with the chosen catalog row's pattern / range / hfov
//
// Threading state from step 2 down to step 4 through props would mean
// touching ~6 files (CCTVDesignerV2 → DesignerToolbar → useDesignerCRUD
// → useDragHandlers → useDesignerInteraction → addEquipment caller).
// Module-level ref is the pragmatic ship-it-today choice — addEquipment
// reads + clears it on every call. Single designer instance per page,
// no concurrency, and it self-clears after use so a stale pick can't
// silently leak into the next equipment placement.
// ────────────────────────────────────────────────────────────────────────────
let pendingSensorOverride: DetectionSensorCatalogEntry | null = null;

export function setPendingDetectionSensor(
  entry: DetectionSensorCatalogEntry | null,
): void {
  pendingSensorOverride = entry;
}

/** Returns and clears the pending entry. Called from addEquipment. */
export function consumePendingDetectionSensor(): DetectionSensorCatalogEntry | null {
  const e = pendingSensorOverride;
  pendingSensorOverride = null;
  return e;
}

/** Peek without clearing — used by beam-pair flow which needs the catalog
 *  entry across two consecutive addEquipment calls (TX then RX). */
export function peekPendingDetectionSensor(): DetectionSensorCatalogEntry | null {
  return pendingSensorOverride;
}

// ────────────────────────────────────────────────────────────────────────────
// Beam-pair handshake.
//
// Beam barriers (Optex AX-70/130/200, SL-100/200 TNR) are two physical
// devices on opposite ends of a detection line. UX is two clicks: first
// places the transmitter, second places the receiver. After the first
// click we stash the TX equipment id here so the next addEquipment call
// knows to create the RX side and link them together.
//
// Same module-level ref pattern as pendingSensorOverride — single
// designer instance per page, self-clears when the pair completes. If
// the user abandons the placement (picks a different tool, opens a new
// catalog), the orphan TX stays on the map; they can delete it manually.
// ────────────────────────────────────────────────────────────────────────────
let pendingBeamTxId: string | null = null;

export function setPendingBeamTxId(id: string | null): void {
  pendingBeamTxId = id;
}
export function consumePendingBeamTxId(): string | null {
  const id = pendingBeamTxId;
  pendingBeamTxId = null;
  return id;
}
export function peekPendingBeamTxId(): string | null {
  return pendingBeamTxId;
}

export function isPickerVisible(entry: DetectionSensorCatalogEntry): boolean {
  if (entry.phase2Beam || entry.phase3Fence) return false;
  return SUPPORTED_PATTERNS_PHASE1.includes(entry.pattern);
}

/**
 * Synthetic DetectionSensorCatalogEntry for a "Generic <X>" quick-pick.
 * The addEquipment hook reads `pattern` to decide cone vs full vs beam-pair
 * flow vs fence-attach — so a generic boundary preset MUST set pattern
 * 'boundary' and a generic beam preset MUST set pattern 'beam', otherwise
 * everything collapses to a default wall-PIR cone. Conservative defaults
 * mirror typical Optex/SATEL spec ranges; the user can dial each instance
 * via the edit panel after placement.
 */
export function buildGenericDetectionSensor(
  category:
    | 'outdoor-wall-pir' | 'indoor-ceiling-pir' | 'curtain-pir'
    | 'boundary-pir' | 'beam-barrier' | 'fence-detector'
    | 'fall-detection-radar',
): DetectionSensorCatalogEntry {
  const base = {
    id: `generic-${category}`,
    manufacturer: 'Generic',
    model: '',
    category,
    verified: false,
    // Synthetic quick-pick entries never render in picker cards, but the
    // catalog entry type requires a tagline.
    tagline: '',
  };
  switch (category) {
    case 'outdoor-wall-pir':
      return {
        ...base,
        model: 'Wall PIR',
        pattern: 'cone',
        rangeM: 12,
        hfovDeg: 90,
        mountingHeightDefaultM: 1.0,
        mountingHeightMinM: 0.8,
        mountingHeightMaxM: 2.4,
        mounting: ['wall', 'pole'],
        detectionMethod: 'pir',
      };
    case 'indoor-ceiling-pir':
      return {
        ...base,
        model: 'Ceiling 360°',
        pattern: 'full',
        rangeM: 6,
        hfovDeg: 360,
        mountingHeightDefaultM: 2.7,
        mountingHeightMinM: 2.4,
        mountingHeightMaxM: 4.0,
        mounting: ['ceiling'],
        detectionMethod: 'pir',
      };
    case 'curtain-pir':
      return {
        ...base,
        model: 'Curtain',
        pattern: 'curtain',
        rangeM: 5,
        hfovDeg: 15,
        mountingHeightDefaultM: 1.2,
        mountingHeightMinM: 0.8,
        mountingHeightMaxM: 2.4,
        mounting: ['wall'],
        detectionMethod: 'pir',
      };
    case 'boundary-pir':
      return {
        ...base,
        model: 'Boundary',
        pattern: 'boundary',
        rangeM: 8,
        hfovDeg: 180,
        mountingHeightDefaultM: 1.0,
        mountingHeightMinM: 0.8,
        mountingHeightMaxM: 2.4,
        mounting: ['wall'],
        detectionMethod: 'pir',
      };
    case 'beam-barrier':
      return {
        ...base,
        model: 'Beam pair',
        pattern: 'beam',
        rangeM: 30,
        hfovDeg: 0,
        mountingHeightDefaultM: 1.0,
        mountingHeightMinM: 0.3,
        mountingHeightMaxM: 4.0,
        mounting: ['wall', 'pole'],
        detectionMethod: 'photoelectric',
      };
    case 'fence-detector':
      return {
        ...base,
        model: 'Fence-tension',
        pattern: 'fence',
        rangeM: 100,
        hfovDeg: 0,
        mountingHeightDefaultM: 1.5,
        mountingHeightMinM: 0.5,
        mountingHeightMaxM: 3.0,
        mounting: ['fence'],
        detectionMethod: 'fence-tension',
      };
    case 'fall-detection-radar':
      // Wall-mount mmWave fall radar mirroring the Hikvision DS-TDSB00
      // class: 2 m wall mount tilted down, ±45° FOV → 90° cone, ~4 m
      // detection depth into the room.
      return {
        ...base,
        model: 'Fall-detection radar',
        pattern: 'cone',
        rangeM: 4,
        hfovDeg: 90,
        mountingHeightDefaultM: 2.0,
        mountingHeightMinM: 1.8,
        mountingHeightMaxM: 2.4,
        mounting: ['wall'],
        detectionMethod: 'radar',
      };
  }
}

/**
 * Builds the partial Equipment payload from a catalog row. Caller spreads
 * this into a fresh Equipment with a generated id, lat/lng from the drop
 * point, and the existing label naming logic.
 *
 * Why partial: the parent component owns lat/lng/id/label generation
 * (those depend on the drop coordinates + existing sensor count), so we
 * only fill the pattern-specific fields here.
 */
export function equipmentDefaultsFromCatalog(
  entry: DetectionSensorCatalogEntry,
): {
  type: 'pir-motion-sensor';
  pirPattern: 'full' | 'cone' | 'curtain' | 'boundary' | 'beam' | 'fence';
  pirRange: number;
  pirHfov: number;
  pirRotation: number;
  pirShowArrow: boolean;
  mountingHeightM: number;
  catalogId: string;
  catalogManufacturer: string;
  catalogModel: string;
  color: string;
  size: 'normal';
} | null {
  if (!isPickerVisible(entry)) return null;
  const pirPattern = entry.pattern as 'full' | 'cone' | 'curtain' | 'boundary' | 'beam' | 'fence';
  return {
    type: 'pir-motion-sensor',
    pirPattern,
    // Range = max detection distance. For beams this is the TX↔RX line
    // length the device supports; renderer uses it to gate the second
    // click in the pair flow.
    pirRange: entry.rangeM,
    // 360° (full) and beam ignore hfov; cone gets the wide angle;
    // curtain/boundary fall through to the renderer's defaults so a 0
    // catalog value (beam entries) doesn't poison the cone math.
    pirHfov: entry.hfovDeg > 0 && entry.hfovDeg < 360 ? entry.hfovDeg : 90,
    pirRotation: 0,
    // Direction arrow is meaningless for 360°, beam endpoints (the
    // line itself is the direction) and fence-tension (the linked
    // fence is the visualisation).
    pirShowArrow:
      pirPattern !== 'full' && pirPattern !== 'beam' && pirPattern !== 'fence',
    mountingHeightM: entry.mountingHeightDefaultM,
    catalogId: entry.id,
    catalogManufacturer: entry.manufacturer,
    catalogModel: entry.model,
    color: '#f59e0b',
    size: 'normal',
  };
}
